import { getFacebookAccessToken } from '@/utils/facebookAuth';
import axios from 'axios';
import { useState } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { Mail, Lock, LogIn, UserPlus, ArrowRight, ArrowDown } from 'lucide-react'
import { getGoogleAccessToken } from '@/utils/googleAuth'
import { CameraCaptureModal } from '@/components/CameraCaptureModal'
import { FileUtils } from '@/utils/fileUtils'

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.55-.2-2.27H12v4.3h6.47a5.52 5.52 0 0 1-2.4 3.62v3.01h3.88c2.28-2.1 3.54-5.2 3.54-8.66Z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.07 7.93-2.9l-3.88-3.01c-1.07.72-2.44 1.15-4.05 1.15-3.11 0-5.74-2.1-6.68-4.92H1.31v3.1A12 12 0 0 0 12 24Z"
    />
    <path
      fill="#FBBC05"
      d="M5.32 14.32A7.2 7.2 0 0 1 4.95 12c0-.8.14-1.58.37-2.32v-3.1H1.31A12 12 0 0 0 0 12c0 1.93.46 3.76 1.31 5.42l4.01-3.1Z"
    />
    <path
      fill="#EA4335"
      d="M12 4.77c1.76 0 3.34.6 4.58 1.78l3.43-3.43C17.94 1.17 15.24 0 12 0A12 12 0 0 0 1.31 6.58l4.01 3.1C6.26 6.86 8.89 4.77 12 4.77Z"
    />
  </svg>
)

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="#1877F2"
      d="M24 12A12 12 0 1 0 10.13 23.87V15.5H7.08V12h3.05V9.34c0-3.02 1.8-4.69 4.56-4.69 1.32 0 2.7.24 2.7.24v2.97h-1.52c-1.5 0-1.97.93-1.97 1.89V12h3.35l-.54 3.5h-2.81v8.37A12 12 0 0 0 24 12Z"
    />
    <path fill="#fff" d="m16.71 15.5.54-3.5H13.9V9.75c0-.96.47-1.89 1.97-1.89h1.52V4.89s-1.38-.24-2.7-.24c-2.76 0-4.56 1.67-4.56 4.69V12H7.08v3.5h3.05v8.37a12.08 12.08 0 0 0 3.77 0V15.5h2.81Z" />
  </svg>
)

export function AuthScreen() {
  const { login, googleAuth, loading, error } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [logoError, setLogoError] = useState(false)
  const [socialMessage, setSocialMessage] = useState('')
  const [showGoogleCamera, setShowGoogleCamera] = useState(false)
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(null)
  const navigate = useNavigate()

  const handleContinue = async () => {
    if (!email.trim() || !password.trim()) return

    try {
      await login({
        email,
        password,
      })
      navigate('/dashboard')
    } catch (err) {
      console.error('Login error:', err)
    }
  }

  const handleForgotPassword = () => {
    navigate('/forgot-password')
  }

  const handleRegister = () => {
    navigate('/register')
  }
  const FACEBOOK_APP_ID = '994736052446478'; // ejemplo, usar variable de entorno en producción
  const handleFacebookLogin = async () => {
    let accessToken = '';
    try {
      setSocialMessage('');
      accessToken = await getFacebookAccessToken(FACEBOOK_APP_ID);
      // Llama al backend igual que Google
      const { data } = await axios.post('/api/auth/facebook/worker', { accessToken });
      // Suponiendo que el backend responde igual que Google
      if (data && data.data && data.data.accessToken) {
        // Puedes guardar el token o llamar a login con el usuario
        navigate('/dashboard');
      }
    } catch (err: any) {
      setSocialMessage(err?.response?.data?.message || err?.message || 'No se pudo iniciar con Facebook');
    }
  };
  const handleGoogleLogin = async () => {
    let accessToken = ''
    try {
      setSocialMessage('')
      const clientId = "845393814372-pcrq4dtk46tetbh3o9kf4hqtskd2agaa.apps.googleusercontent.com"
      accessToken = await getGoogleAccessToken(clientId)
      setGoogleAccessToken(accessToken)

      // Si el usuario ya tiene foto validada en backend, entra sin abrir camara.
      await googleAuth({ accessToken })
      setShowGoogleCamera(false)
      setGoogleAccessToken(null)
      navigate('/dashboard')
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'No se pudo iniciar con Google'
      const requiresCamera = typeof message === 'string' && message.toLowerCase().includes('camera photo is required')

      if (requiresCamera && accessToken) {
        setGoogleAccessToken(accessToken)
        setShowGoogleCamera(true)
      } else {
        setSocialMessage(message)
      }
    }
  }

  const handleGoogleCameraSubmit = async (photoFile: File) => {
    if (!googleAccessToken) {
      setSocialMessage('No se pudo validar el token de Google. Intenta nuevamente.')
      return
    }

    try {
      const profileImageBase64 = await FileUtils.fileToBase64(photoFile)
      await googleAuth({
        accessToken: googleAccessToken,
        profileImageBase64,
        profileImageName: photoFile.name,
        profileImageMimeType: photoFile.type,
      })
      setShowGoogleCamera(false)
      setGoogleAccessToken(null)
      navigate('/dashboard')
    } catch (err: any) {
      setSocialMessage(err?.response?.data?.message || err?.message || 'No se pudo iniciar con Google')
    }
  }

  const handleSocialLogin = (provider: 'Facebook') => {
    if (provider === 'Facebook') {
      handleFacebookLogin();
    }
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        style={{
          minHeight: '100vh',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #18132b 0%, #312e81 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Gradientes y efectos de fondo */}
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }}>
          {/* Morado/azul superior suave */}
          <div style={{
            position: 'absolute',
            top: '-120px',
            left: '50%',
            width: '700px',
            height: '340px',
            transform: 'translateX(-50%)',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.22) 0%, transparent 80%)',
            filter: 'blur(90px)',
          }} />
          {/* Azul inferior derecho */}
          <div style={{
            position: 'absolute',
            bottom: '-120px',
            right: '-80px',
            width: '400px',
            height: '300px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 80%)',
            filter: 'blur(90px)',
          }} />
        </div>
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.15 }}
          style={{
            width: '100%',
            maxWidth: '960px',
            borderRadius: '28px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.12)',
            background: 'rgba(15, 23, 42, 0.72)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 24px 60px rgba(2, 6, 23, 0.45)',
            display: 'grid',
            gridTemplateColumns: '1fr',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr' }}>
          <section
            style={{
              padding: '40px 32px 32px 32px',
              color: '#fff',
              borderBottom: '1px solid rgba(255,255,255,0.10)',
              background: 'transparent',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              {!logoError ? (
                <img
                  src="/logo.png"
                  alt="WingDrive"
                  style={{ height: '110px', width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 0 32px #facc15cc)' }}
                  onError={() => setLogoError(true)}
                />
              ) : (
                <p style={{ fontSize: '32px', fontWeight: 800, color: '#67e8f9', letterSpacing: '0.04em', textShadow: '0 0 18px #0ea5e9' }}>WINGDRIVE</p>
              )}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span style={{
                display: 'inline-block',
                marginTop: '18px',
                padding: '7px 18px',
                borderRadius: '999px',
                border: '2px solid #38bdf8',
                background: 'rgba(59,130,246,0.13)',
                fontSize: '14px',
                fontWeight: 900,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: '#38bdf8',
                boxShadow: '0 0 18px #38bdf8cc',
                textShadow: '0 0 8px #38bdf8cc',
              }}>
                ¡Bienvenido a tu futuro!
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              style={{
                marginTop: '18px',
                fontSize: '2.7rem',
                lineHeight: 1.1,
                fontWeight: 900,
                letterSpacing: '-0.01em',
                color: '#fff',
                fontFamily: 'Poppins, Inter, sans-serif',
                textShadow: '0 0 40px rgba(108,43,217,0.8)',
              }}
            >
              EL HÉROE<br />
              <span style={{
                background: 'linear-gradient(135deg, #6C2BD9, #FACC15)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 40px rgba(108,43,217,0.8), 0 0 20px rgba(250,204,21,0.6)',
                display: 'inline-block',
              }}>ERES TÚ</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              style={{
                marginTop: '14px',
                color: '#fff',
                fontSize: '1.2rem',
                maxWidth: '600px',
                marginLeft: 'auto',
                marginRight: 'auto',
                textShadow: '0 0 40px rgba(108,43,217,0.8)',
                fontWeight: 700,
                letterSpacing: '0.01em',
              }}
            >
              Tu poder está al volante
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              style={{
                marginTop: '10px',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '1rem',
                maxWidth: '600px',
                marginLeft: 'auto',
                marginRight: 'auto',
                textShadow: '0 0 18px #6C2BD9',
                fontWeight: 400,
                letterSpacing: '0.01em',
              }}
            >
Accede a oportunidades reales, conecta con empresas y haz despegar tu carrera. ¡Tu viaje comienza aquí!
            </motion.p>
          </section>
          {/* Flecha animada para invitar a bajar y registrarse (ahora antes de Iniciar sesión) */}
          <motion.div
            initial={{ opacity: 0, y: 0 }}
            animate={{ opacity: 1, y: [0, 18, 0, -8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              margin: '0 auto 0 auto',
              width: 'fit-content',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0',
              position: 'relative',
              top: '18px',
              zIndex: 2,
            }}
          >
            <ArrowDown size={48} style={{
              color: '#fff',
              filter: 'drop-shadow(0 0 16px #6C2BD9)',
              background: 'transparent',
              borderRadius: '50%',
              padding: '4px',
            }} />
            <span style={{ display: 'block', background: 'linear-gradient(135deg, #6C2BD9, #FACC15)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 900, fontSize: '15px', marginTop: '-8px', textShadow: '0 0 8px #6C2BD9, 0 0 8px #FACC15', letterSpacing: '0.13em', textTransform: 'uppercase' }}>
              Baja y regístrate para continuar
            </span>
          </motion.div>

          <section style={{ background: '#fff', padding: '28px' }}>
            <div style={{ maxWidth: '440px', margin: '0 auto' }}>
              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: 900,
                  background: 'linear-gradient(90deg, #6366f1 10%, #0ea5e9 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 18px #6366f1cc, 0 0 8px #0ea5e9cc',
                  letterSpacing: '0.01em',
                  marginBottom: '0',
                }}
              >
                Iniciar sesión
              </h2>
              <p style={{ marginTop: '4px', color: '#475569', fontSize: '14px' }}>Accede como conductor para continuar.</p>

              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  handleContinue()
                }}
                style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}
              >
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                    Correo electronico
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="email"
                      placeholder="tu@correo.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{
                        width: '100%',
                        height: '44px',
                        borderRadius: '12px',
                        border: '1px solid #cbd5e1',
                        padding: '0 12px 0 36px',
                        fontSize: '14px',
                        color: '#0f172a',
                      }}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 600, color: '#334155' }}>
                    Contrasena
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Lock size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                    <input
                      type="password"
                      placeholder="********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        width: '100%',
                        height: '44px',
                        borderRadius: '12px',
                        border: '1px solid #cbd5e1',
                        padding: '0 12px 0 36px',
                        fontSize: '14px',
                        color: '#0f172a',
                      }}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    style={{ color: '#0369a1', fontSize: '13px', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '2px' }}
                  >
                    Olvide mi contrasena
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={!email.trim() || !password.trim() || loading}
                  style={{
                    height: '46px',
                    borderRadius: '12px',
                    border: 'none',
                    background: 'linear-gradient(90deg, #f59e0b, #facc15)',
                    color: '#0f172a',
                    fontWeight: 800,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    opacity: !email.trim() || !password.trim() || loading ? 0.55 : 1,
                  }}
                >
                  <LogIn size={16} />
                  {loading ? 'Iniciando sesion...' : 'Ingresar'}
                  <ArrowRight size={16} />
                </button>
              </form>

              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    marginTop: '10px',
                    color: '#b91c1c',
                    fontSize: '13px',
                    textAlign: 'center',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    padding: '8px',
                    borderRadius: '10px',
                  }}
                >
                  {error}
                </motion.p>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '18px 0' }}>
                <span style={{ height: '1px', background: '#e2e8f0', flex: 1 }} />
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  o continuar con
                </span>
                <span style={{ height: '1px', background: '#e2e8f0', flex: 1 }} />
              </div>

              <div style={{ display: 'grid', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  style={{
                    height: '44px',
                    borderRadius: '12px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    color: '#334155',
                    fontSize: '14px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                  }}
                >
                  <GoogleIcon />
                  Iniciar sesion con Google
                </button>

                {/* <button
                  type="button"
                  onClick={() => handleSocialLogin('Facebook')}
                  style={{
                    height: '44px',
                    borderRadius: '12px',
                    border: '1px solid #1877F2',
                    background: '#1877F2',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                  }}
                >
                  <FacebookIcon />
                  Iniciar sesion con Facebook
                </button> */}
              </div>

              {socialMessage && (
                <p style={{ marginTop: '10px', fontSize: '12px', color: '#0f766e', background: '#ccfbf1', border: '1px solid #99f6e4', borderRadius: '8px', padding: '8px 10px' }}>
                  {socialMessage}
                </p>
              )}

              <div style={{ marginTop: '18px', textAlign: 'center' }}>
                <p style={{ fontSize: '14px', color: '#475569', marginBottom: '10px' }}>No tienes una cuenta?</p>
                <button
                  type="button"
                  onClick={handleRegister}
                  style={{
                    width: '100%',
                    height: '44px',
                    borderRadius: '12px',
                    border: '1px solid #cbd5e1',
                    background: '#f8fafc',
                    color: '#334155',
                    fontSize: '14px',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                >
                  <UserPlus size={16} />
                  Crear cuenta nueva
                </button>
              </div>
            </div>
          </section>
          </div>
        </motion.div>
      </motion.div>
      <CameraCaptureModal
        open={showGoogleCamera}
        loading={loading}
        title="Validacion de identidad"
        description="Para continuar con Google debes tomar una foto con camara. No se usara la foto de Google."
        onCancel={() => {
          setShowGoogleCamera(false)
          setGoogleAccessToken(null)
        }}
        onCaptured={handleGoogleCameraSubmit}
      />
    </>
  )
}
